import * as i0 from '@angular/core';
import { Injectable, Component, ChangeDetectionStrategy, HostBinding, NgModule, inject, Optional, ChangeDetectorRef, ViewChild } from '@angular/core';
import * as i2 from '@angular/forms';
import { UntypedFormGroup, UntypedFormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import * as i1 from '@spartacus/core';
import { GlobalMessageType, UrlModule, I18nModule, FeaturesConfigModule, provideDefaultConfig, NotAuthGuard, AuthService, GlobalMessageService, WindowRef, RoutingService, useFeatureStyles, AuthGuard } from '@spartacus/core';
import * as i4 from '@spartacus/storefront';
import { CustomFormValidators, FormErrorsModule, SpinnerModule, PasswordVisibilityToggleModule, PageSlotModule, DIALOG_TYPE, LaunchDialogService, KeyboardFocusModule, IconModule } from '@spartacus/storefront';
import { BehaviorSubject, from, of, tap as tap$1 } from 'rxjs';
import { tap, withLatestFrom, switchMap } from 'rxjs/operators';
import * as i1$1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i1$2 from '@angular/router';
import { RouterModule } from '@angular/router';
import * as i2$1 from '@spartacus/user/account/root';
import { VerificationTokenFacade, VERIFICATION_TOKEN_DIALOG_ACTION } from '@spartacus/user/account/root';

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
class LoginFormComponentService {
    constructor(auth, globalMessage, winRef) {
        this.auth = auth;
        this.globalMessage = globalMessage;
        this.winRef = winRef;
        this.busy$ = new BehaviorSubject(false);
        this.isUpdating$ = this.busy$.pipe(tap((state) => {
            const userId = this.winRef.nativeWindow?.history?.state?.['newUid'];
            if (userId) {
                this.form.patchValue({ userId });
            }
            state === true ? this.form.disable() : this.form.enable();
        }));
        this.form = new UntypedFormGroup({
            userId: new UntypedFormControl('', [
                Validators.required,
                CustomFormValidators.emailValidator,
            ]),
            password: new UntypedFormControl('', Validators.required),
        });
    }
    login() {
        if (!this.form.valid) {
            this.form.markAllAsTouched();
            return;
        }
        this.busy$.next(true);
        from(this.auth.loginWithCredentials(
        // TODO: consider dropping toLowerCase as this should not be part of the UI,
        // as it's too opinionated and doesn't work with other AUTH services
        this.form.value.userId.toLowerCase(), this.form.value.password))
            .pipe(withLatestFrom(this.auth.isUserLoggedIn()), tap(([_, isLoggedIn]) => this.onSuccess(isLoggedIn)))
            .subscribe();
    }
    onSuccess(isLoggedIn) {
        if (isLoggedIn) {
            // We want to remove error messages on successful login (primary the bad
            // username/password combination)
            this.globalMessage.remove(GlobalMessageType.MSG_TYPE_ERROR);
            this.form.reset();
        }
        this.busy$.next(false);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: LoginFormComponentService, deps: [{ token: i1.AuthService }, { token: i1.GlobalMessageService }, { token: i1.WindowRef }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: LoginFormComponentService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: LoginFormComponentService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i1.AuthService }, { type: i1.GlobalMessageService }, { type: i1.WindowRef }] });

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
class LoginFormComponent {
    constructor(service) {
        this.service = service;
        this.form = this.service.form;
        this.isUpdating$ = this.service.isUpdating$;
        this.style = true;
    }
    onSubmit() {
        this.service.login();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: LoginFormComponent, deps: [{ token: LoginFormComponentService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.5", type: LoginFormComponent, selector: "cx-login-form", host: { properties: { "class.user-form": "this.style" } }, ngImport: i0, template: "<cx-spinner class=\"overlay\" *ngIf=\"isUpdating$ | async\"></cx-spinner>\n\n<form (ngSubmit)=\"onSubmit()\" [formGroup]=\"form\">\n  <!-- TODO: (CXSPA-5953) Remove feature flags next major -->\n  <label>\n    <span class=\"label-content\">\n      {{ 'loginForm.emailAddress.label' | cxTranslate }}\n    </span>\n    <input\n      required=\"true\"\n      type=\"email\"\n      class=\"form-control\"\n      formControlName=\"userId\"\n      placeholder=\"{{ 'loginForm.emailAddress.placeholder' | cxTranslate }}\"\n    />\n\n    <!-- TODO: (CXSPA-7315) Remove feature toggle in the next major -->\n    <cx-form-errors\n      *cxFeature=\"'formErrorsDescriptiveMessages'\"\n      [translationParams]=\"{\n        label: 'loginForm.emailAddress.label' | cxTranslate,\n      }\"\n      [control]=\"form.get('userId')\"\n    ></cx-form-errors>\n\n    <cx-form-errors\n      *cxFeature=\"'!formErrorsDescriptiveMessages'\"\n      [control]=\"form.get('userId')\"\n    ></cx-form-errors>\n  </label>\n\n  <label>\n    <span class=\"label-content\">\n      {{ 'loginForm.password.label' | cxTranslate }}\n    </span>\n    <input\n      required=\"true\"\n      type=\"password\"\n      class=\"form-control\"\n      placeholder=\"{{ 'loginForm.password.placeholder' | cxTranslate }}\"\n      formControlName=\"password\"\n      [attr.aria-label]=\"'loginForm.password.placeholder' | cxTranslate\"\n      cxPasswordVisibilitySwitch\n    />\n\n    <!-- TODO: (CXSPA-7315) Remove feature toggle in the next major -->\n    <cx-form-errors\n      *cxFeature=\"'formErrorsDescriptiveMessages'\"\n      [translationParams]=\"{\n        label: 'loginForm.password.label' | cxTranslate,\n      }\"\n      [control]=\"form.get('password')\"\n    ></cx-form-errors>\n\n    <cx-form-errors\n      *cxFeature=\"'!formErrorsDescriptiveMessages'\"\n      [control]=\"form.get('password')\"\n    ></cx-form-errors>\n  </label>\n\n  <a [routerLink]=\"{ cxRoute: 'forgotPassword' } | cxUrl\" class=\"btn-link\">\n    {{ 'loginForm.forgotPassword' | cxTranslate }}\n  </a>\n\n  <button\n    type=\"submit\"\n    class=\"btn btn-block btn-primary\"\n    [disabled]=\"form.disabled\"\n  >\n    {{ 'loginForm.signIn' | cxTranslate }}\n  </button>\n</form>\n\n<ng-template #requiredAsterisk>\n  <abbr\n    *cxFeature=\"'a11yRequiredAsterisks'\"\n    class=\"text-decoration-none\"\n    title=\"{{ 'common.required' | cxTranslate }}\"\n    >*</abbr\n  >\n</ng-template>\n", dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i2.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { kind: "directive", type: i2.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i2.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "directive", type: i1$2.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "component", type: i4.FormErrorsComponent, selector: "cx-form-errors", inputs: ["prefix", "fallbackPrefix", "translationParams", "control"] }, { kind: "component", type: i4.SpinnerComponent, selector: "cx-spinner" }, { kind: "directive", type: i4.PasswordVisibilityToggleDirective, selector: "[cxPasswordVisibilitySwitch][type=\"password\"]" }, { kind: "directive", type: i1.FeatureDirective, selector: "[cxFeature]", inputs: ["cxFeature"] }, { kind: "pipe", type: i1$1.AsyncPipe, name: "async" }, { kind: "pipe", type: i1.UrlPipe, name: "cxUrl" }, { kind: "pipe", type: i1.TranslatePipe, name: "cxTranslate" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: LoginFormComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cx-login-form', changeDetection: ChangeDetectionStrategy.OnPush, template: "<cx-spinner class=\"overlay\" *ngIf=\"isUpdating$ | async\"></cx-spinner>\n\n<form (ngSubmit)=\"onSubmit()\" [formGroup]=\"form\">\n  <!-- TODO: (CXSPA-5953) Remove feature flags next major -->\n  <label>\n    <span class=\"label-content\">\n      {{ 'loginForm.emailAddress.label' | cxTranslate }}\n    </span>\n    <input\n      required=\"true\"\n      type=\"email\"\n      class=\"form-control\"\n      formControlName=\"userId\"\n      placeholder=\"{{ 'loginForm.emailAddress.placeholder' | cxTranslate }}\"\n    />\n\n    <!-- TODO: (CXSPA-7315) Remove feature toggle in the next major -->\n    <cx-form-errors\n      *cxFeature=\"'formErrorsDescriptiveMessages'\"\n      [translationParams]=\"{\n        label: 'loginForm.emailAddress.label' | cxTranslate,\n      }\"\n      [control]=\"form.get('userId')\"\n    ></cx-form-errors>\n\n    <cx-form-errors\n      *cxFeature=\"'!formErrorsDescriptiveMessages'\"\n      [control]=\"form.get('userId')\"\n    ></cx-form-errors>\n  </label>\n\n  <label>\n    <span class=\"label-content\">\n      {{ 'loginForm.password.label' | cxTranslate }}\n    </span>\n    <input\n      required=\"true\"\n      type=\"password\"\n      class=\"form-control\"\n      placeholder=\"{{ 'loginForm.password.placeholder' | cxTranslate }}\"\n      formControlName=\"password\"\n      [attr.aria-label]=\"'loginForm.password.placeholder' | cxTranslate\"\n      cxPasswordVisibilitySwitch\n    />\n\n    <!-- TODO: (CXSPA-7315) Remove feature toggle in the next major -->\n    <cx-form-errors\n      *cxFeature=\"'formErrorsDescriptiveMessages'\"\n      [translationParams]=\"{\n        label: 'loginForm.password.label' | cxTranslate,\n      }\"\n      [control]=\"form.get('password')\"\n    ></cx-form-errors>\n\n    <cx-form-errors\n      *cxFeature=\"'!formErrorsDescriptiveMessages'\"\n      [control]=\"form.get('password')\"\n    ></cx-form-errors>\n  </label>\n\n  <a [routerLink]=\"{ cxRoute: 'forgotPassword' } | cxUrl\" class=\"btn-link\">\n    {{ 'loginForm.forgotPassword' | cxTranslate }}\n  </a>\n\n  <button\n    type=\"submit\"\n    class=\"btn btn-block btn-primary\"\n    [disabled]=\"form.disabled\"\n  >\n    {{ 'loginForm.signIn' | cxTranslate }}\n  </button>\n</form>\n\n<ng-template #requiredAsterisk>\n  <abbr\n    *cxFeature=\"'a11yRequiredAsterisks'\"\n    class=\"text-decoration-none\"\n    title=\"{{ 'common.required' | cxTranslate }}\"\n    >*</abbr\n  >\n</ng-template>\n" }]
        }], ctorParameters: () => [{ type: LoginFormComponentService }], propDecorators: { style: [{
                type: HostBinding,
                args: ['class.user-form']
            }] } });

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
class LoginFormModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: LoginFormModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.5", ngImport: i0, type: LoginFormModule, declarations: [LoginFormComponent], imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            RouterModule,
            UrlModule,
            I18nModule,
            FormErrorsModule,
            SpinnerModule,
            PasswordVisibilityToggleModule,
            FeaturesConfigModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: LoginFormModule, providers: [
            provideDefaultConfig({
                cmsComponents: {
                    ReturningCustomerLoginComponent: {
                        component: LoginFormComponent,
                        guards: [NotAuthGuard],
                        providers: [
                            {
                                provide: LoginFormComponentService,
                                useClass: LoginFormComponentService,
                                deps: [AuthService, GlobalMessageService, WindowRef],
                            },
                        ],
                    },
                },
            }),
        ], imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            RouterModule,
            UrlModule,
            I18nModule,
            FormErrorsModule,
            SpinnerModule,
            PasswordVisibilityToggleModule,
            FeaturesConfigModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: LoginFormModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule,
                        RouterModule,
                        UrlModule,
                        I18nModule,
                        FormErrorsModule,
                        SpinnerModule,
                        PasswordVisibilityToggleModule,
                        FeaturesConfigModule,
                    ],
                    providers: [
                        provideDefaultConfig({
                            cmsComponents: {
                                ReturningCustomerLoginComponent: {
                                    component: LoginFormComponent,
                                    guards: [NotAuthGuard],
                                    providers: [
                                        {
                                            provide: LoginFormComponentService,
                                            useClass: LoginFormComponentService,
                                            deps: [AuthService, GlobalMessageService, WindowRef],
                                        },
                                    ],
                                },
                            },
                        }),
                    ],
                    declarations: [LoginFormComponent],
                }]
        }] });

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
class LoginRegisterComponent {
    constructor(activatedRoute) {
        this.activatedRoute = activatedRoute;
        this.loginAsGuest = false;
        this.routingService = inject(RoutingService, {
            optional: true,
        });
    }
    ngOnInit() {
        this.loginAsGuest = this.activatedRoute.snapshot.queryParams['forced'];
    }
    navigateTo(cxRoute) {
        this.routingService?.go({ cxRoute });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: LoginRegisterComponent, deps: [{ token: i1$2.ActivatedRoute }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.5", type: LoginRegisterComponent, selector: "cx-login-register", ngImport: i0, template: "<div class=\"register\">\n  <p class=\"cx-section-title\">\n    {{ 'loginForm.dontHaveAccount' | cxTranslate }}\n  </p>\n\n  <!-- TODO: (CXSPA-7252) - Remove feature flag next major release -->\n  <ng-container *cxFeature=\"'a11yUseButtonsForBtnLinks'\">\n    <button\n      (click)=\"navigateTo(loginAsGuest ? 'checkoutLogin' : 'register')\"\n      class=\"btn btn-block btn-secondary\"\n      [ngClass]=\"loginAsGuest ? 'btn-guest' : 'btn-register'\"\n    >\n      {{\n        (loginAsGuest ? 'loginForm.guestCheckout' : 'loginForm.register')\n          | cxTranslate\n      }}\n    </button>\n  </ng-container>\n\n  <ng-container *cxFeature=\"'!a11yUseButtonsForBtnLinks'\">\n    <ng-container *ngIf=\"!loginAsGuest\">\n      <a\n        [routerLink]=\"{ cxRoute: 'register' } | cxUrl\"\n        class=\"btn btn-block btn-secondary btn-register\"\n        >{{ 'loginForm.register' | cxTranslate }}</a\n      >\n    </ng-container>\n\n    <ng-container *ngIf=\"loginAsGuest\">\n      <a\n        [routerLink]=\"{ cxRoute: 'checkoutLogin' } | cxUrl\"\n        class=\"btn btn-block btn-secondary btn-guest\"\n        >{{ 'loginForm.guestCheckout' | cxTranslate }}</a\n      >\n    </ng-container>\n  </ng-container>\n</div>\n", dependencies: [{ kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "directive", type: i1.FeatureDirective, selector: "[cxFeature]", inputs: ["cxFeature"] }, { kind: "pipe", type: i1.UrlPipe, name: "cxUrl" }, { kind: "pipe", type: i1.TranslatePipe, name: "cxTranslate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: LoginRegisterComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cx-login-register', template: "<div class=\"register\">\n  <p class=\"cx-section-title\">\n    {{ 'loginForm.dontHaveAccount' | cxTranslate }}\n  </p>\n\n  <!-- TODO: (CXSPA-7252) - Remove feature flag next major release -->\n  <ng-container *cxFeature=\"'a11yUseButtonsForBtnLinks'\">\n    <button\n      (click)=\"navigateTo(loginAsGuest ? 'checkoutLogin' : 'register')\"\n      class=\"btn btn-block btn-secondary\"\n      [ngClass]=\"loginAsGuest ? 'btn-guest' : 'btn-register'\"\n    >\n      {{\n        (loginAsGuest ? 'loginForm.guestCheckout' : 'loginForm.register')\n          | cxTranslate\n      }}\n    </button>\n  </ng-container>\n\n  <ng-container *cxFeature=\"'!a11yUseButtonsForBtnLinks'\">\n    <ng-container *ngIf=\"!loginAsGuest\">\n      <a\n        [routerLink]=\"{ cxRoute: 'register' } | cxUrl\"\n        class=\"btn btn-block btn-secondary btn-register\"\n        >{{ 'loginForm.register' | cxTranslate }}</a\n      >\n    </ng-container>\n\n    <ng-container *ngIf=\"loginAsGuest\">\n      <a\n        [routerLink]=\"{ cxRoute: 'checkoutLogin' } | cxUrl\"\n        class=\"btn btn-block btn-secondary btn-guest\"\n        >{{ 'loginForm.guestCheckout' | cxTranslate }}</a\n      >\n    </ng-container>\n  </ng-container>\n</div>\n" }]
        }], ctorParameters: () => [{ type: i1$2.ActivatedRoute }], propDecorators: { routingService: [{
                type: Optional
            }] } });

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
class LoginRegisterModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: LoginRegisterModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.5", ngImport: i0, type: LoginRegisterModule, declarations: [LoginRegisterComponent], imports: [CommonModule,
            RouterModule,
            UrlModule,
            PageSlotModule,
            I18nModule,
            FeaturesConfigModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: LoginRegisterModule, providers: [
            provideDefaultConfig({
                cmsComponents: {
                    ReturningCustomerRegisterComponent: {
                        component: LoginRegisterComponent,
                        guards: [NotAuthGuard],
                    },
                },
            }),
        ], imports: [CommonModule,
            RouterModule,
            UrlModule,
            PageSlotModule,
            I18nModule,
            FeaturesConfigModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: LoginRegisterModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        RouterModule,
                        UrlModule,
                        PageSlotModule,
                        I18nModule,
                        FeaturesConfigModule,
                    ],
                    providers: [
                        provideDefaultConfig({
                            cmsComponents: {
                                ReturningCustomerRegisterComponent: {
                                    component: LoginRegisterComponent,
                                    guards: [NotAuthGuard],
                                },
                            },
                        }),
                    ],
                    declarations: [LoginRegisterComponent],
                }]
        }] });

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
class LoginComponent {
    constructor(auth, userAccount) {
        this.auth = auth;
        this.userAccount = userAccount;
        useFeatureStyles('a11yMyAccountLinkOutline');
    }
    ngOnInit() {
        this.user$ = this.auth.isUserLoggedIn().pipe(switchMap((isUserLoggedIn) => {
            if (isUserLoggedIn) {
                return this.userAccount.get();
            }
            else {
                return of(undefined);
            }
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: LoginComponent, deps: [{ token: i1.AuthService }, { token: i2$1.UserAccountFacade }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.5", type: LoginComponent, selector: "cx-login", ngImport: i0, template: "<ng-container *ngIf=\"user$ | async as user; else login\">\n  <div class=\"cx-login-greet\">\n    {{ 'miniLogin.userGreeting' | cxTranslate: { name: user.name } }}\n  </div>\n  <cx-page-slot id=\"account-nav\" position=\"HeaderLinks\"></cx-page-slot>\n</ng-container>\n\n<ng-template #login>\n  <a role=\"link\" [routerLink]=\"{ cxRoute: 'login' } | cxUrl\">{{\n    'miniLogin.signInRegister' | cxTranslate\n  }}</a>\n</ng-template>\n", dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "component", type: i4.PageSlotComponent, selector: "cx-page-slot,[cx-page-slot]", inputs: ["position", "class", "isPageFold", "hasComponents"] }, { kind: "pipe", type: i1$1.AsyncPipe, name: "async" }, { kind: "pipe", type: i1.UrlPipe, name: "cxUrl" }, { kind: "pipe", type: i1.TranslatePipe, name: "cxTranslate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: LoginComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cx-login', template: "<ng-container *ngIf=\"user$ | async as user; else login\">\n  <div class=\"cx-login-greet\">\n    {{ 'miniLogin.userGreeting' | cxTranslate: { name: user.name } }}\n  </div>\n  <cx-page-slot id=\"account-nav\" position=\"HeaderLinks\"></cx-page-slot>\n</ng-container>\n\n<ng-template #login>\n  <a role=\"link\" [routerLink]=\"{ cxRoute: 'login' } | cxUrl\">{{\n    'miniLogin.signInRegister' | cxTranslate\n  }}</a>\n</ng-template>\n" }]
        }], ctorParameters: () => [{ type: i1.AuthService }, { type: i2$1.UserAccountFacade }] });

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
class LoginModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: LoginModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.5", ngImport: i0, type: LoginModule, declarations: [LoginComponent], imports: [CommonModule, RouterModule, UrlModule, PageSlotModule, I18nModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: LoginModule, providers: [
            provideDefaultConfig({
                cmsComponents: {
                    LoginComponent: {
                        component: LoginComponent,
                    },
                },
            }),
        ], imports: [CommonModule, RouterModule, UrlModule, PageSlotModule, I18nModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: LoginModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, RouterModule, UrlModule, PageSlotModule, I18nModule],
                    providers: [
                        provideDefaultConfig({
                            cmsComponents: {
                                LoginComponent: {
                                    component: LoginComponent,
                                },
                            },
                        }),
                    ],
                    declarations: [LoginComponent],
                }]
        }] });

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
class MyAccountV2UserComponent extends LoginComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: MyAccountV2UserComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.5", type: MyAccountV2UserComponent, selector: "cx-my-account-v2-user", usesInheritance: true, ngImport: i0, template: "<div class=\"cx-my-account-v2-user\">\n  <ng-container *ngIf=\"user$ | async as user\">\n    <div class=\"cx-name\">{{ user.title }}{{ user.name }}</div>\n    <a\n      class=\"cx-sign-out\"\n      [routerLink]=\"\n        {\n          cxRoute: 'logout',\n        } | cxUrl\n      \"\n      >{{ 'myAccountV2User.signOut' | cxTranslate }}\n    </a>\n  </ng-container>\n</div>\n", dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$2.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "pipe", type: i1$1.AsyncPipe, name: "async" }, { kind: "pipe", type: i1.UrlPipe, name: "cxUrl" }, { kind: "pipe", type: i1.TranslatePipe, name: "cxTranslate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: MyAccountV2UserComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cx-my-account-v2-user', template: "<div class=\"cx-my-account-v2-user\">\n  <ng-container *ngIf=\"user$ | async as user\">\n    <div class=\"cx-name\">{{ user.title }}{{ user.name }}</div>\n    <a\n      class=\"cx-sign-out\"\n      [routerLink]=\"\n        {\n          cxRoute: 'logout',\n        } | cxUrl\n      \"\n      >{{ 'myAccountV2User.signOut' | cxTranslate }}\n    </a>\n  </ng-container>\n</div>\n" }]
        }] });

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
class MyAccountV2UserModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: MyAccountV2UserModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.5", ngImport: i0, type: MyAccountV2UserModule, declarations: [MyAccountV2UserComponent], imports: [CommonModule, RouterModule, UrlModule, I18nModule], exports: [MyAccountV2UserComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: MyAccountV2UserModule, providers: [
            provideDefaultConfig({
                cmsComponents: {
                    MyAccountViewUserComponent: {
                        component: MyAccountV2UserComponent,
                        guards: [AuthGuard],
                    },
                },
            }),
        ], imports: [CommonModule, RouterModule, UrlModule, I18nModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: MyAccountV2UserModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [
                        provideDefaultConfig({
                            cmsComponents: {
                                MyAccountViewUserComponent: {
                                    component: MyAccountV2UserComponent,
                                    guards: [AuthGuard],
                                },
                            },
                        }),
                    ],
                    declarations: [MyAccountV2UserComponent],
                    exports: [MyAccountV2UserComponent],
                    imports: [CommonModule, RouterModule, UrlModule, I18nModule],
                }]
        }] });

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
const ONE_TIME_PASSWORD_LOGIN_PURPOSE = 'LOGIN';

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
class OneTimePasswordLoginFormComponent {
    constructor() {
        this.routingService = inject(RoutingService);
        this.verificationTokenFacade = inject(VerificationTokenFacade);
        this.winRef = inject(WindowRef);
        this.busy$ = new BehaviorSubject(false);
        this.isUpdating$ = this.busy$.pipe(tap$1((state) => {
            const userId = this.winRef.nativeWindow?.history?.state?.['newUid'];
            if (userId) {
                this.form.patchValue({ userId });
            }
            state === true ? this.form.disable() : this.form.enable();
        }));
        this.form = new UntypedFormGroup({
            userId: new UntypedFormControl('', [
                Validators.required,
                CustomFormValidators.emailValidator,
            ]),
            password: new UntypedFormControl('', Validators.required),
        });
        this.style = true;
    }
    onSubmit() {
        if (!this.form.valid) {
            this.form.markAllAsTouched();
            return;
        }
        this.busy$.next(true);
        const verificationTokenCreation = this.collectDataFromLoginForm();
        this.verificationTokenFacade
            .createVerificationToken(verificationTokenCreation)
            .subscribe({
            next: (result) => this.goToVerificationTokenForm(result, verificationTokenCreation),
            error: () => this.busy$.next(false),
            complete: () => this.onCreateVerificationTokenComplete(),
        });
    }
    goToVerificationTokenForm(verificationToken, verificationTokenCreation) {
        this.routingService.go({
            cxRoute: 'verifyToken',
        }, {
            state: {
                loginId: verificationTokenCreation.loginId,
                password: verificationTokenCreation.password,
                tokenId: verificationToken.tokenId,
                expiresIn: verificationToken.expiresIn,
            },
        });
    }
    onCreateVerificationTokenComplete() {
        this.form.reset();
        this.busy$.next(false);
    }
    collectDataFromLoginForm() {
        return {
            // TODO: consider dropping toLowerCase as this should not be part of the UI,
            // as it's too opinionated and doesn't work with other AUTH services
            loginId: this.form.value.userId.toLowerCase(),
            password: this.form.value.password,
            purpose: ONE_TIME_PASSWORD_LOGIN_PURPOSE,
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: OneTimePasswordLoginFormComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.5", type: OneTimePasswordLoginFormComponent, selector: "cx-otp-login-form", host: { properties: { "class.user-form": "this.style" } }, ngImport: i0, template: "<cx-spinner class=\"overlay\" *ngIf=\"isUpdating$ | async\"></cx-spinner>\n\n<form (ngSubmit)=\"onSubmit()\" [formGroup]=\"form\">\n  <!-- TODO: (CXSPA-5953) Remove feature flags next major -->\n  <p *cxFeature=\"'a11yRequiredAsterisks'\" class=\"form-legend\">\n    {{ 'formLegend.required' | cxTranslate }}\n  </p>\n  <label>\n    <span class=\"label-content\">\n      {{ 'loginForm.emailAddress.label' | cxTranslate }}\n      <ng-template [ngTemplateOutlet]=\"requiredAsterisk\"></ng-template>\n    </span>\n    <input\n      required=\"true\"\n      type=\"email\"\n      class=\"form-control\"\n      formControlName=\"userId\"\n      placeholder=\"{{ 'loginForm.emailAddress.placeholder' | cxTranslate }}\"\n    />\n    <cx-form-errors [control]=\"form.get('userId')\"></cx-form-errors>\n  </label>\n\n  <label>\n    <span class=\"label-content\">\n      {{ 'loginForm.password.label' | cxTranslate }}\n      <ng-template [ngTemplateOutlet]=\"requiredAsterisk\"></ng-template>\n    </span>\n    <input\n      required=\"true\"\n      type=\"password\"\n      class=\"form-control\"\n      placeholder=\"{{ 'loginForm.password.placeholder' | cxTranslate }}\"\n      formControlName=\"password\"\n      [attr.aria-label]=\"'loginForm.password.placeholder' | cxTranslate\"\n      cxPasswordVisibilitySwitch\n    />\n    <cx-form-errors [control]=\"form.get('password')\"></cx-form-errors>\n  </label>\n\n  <a [routerLink]=\"{ cxRoute: 'forgotPassword' } | cxUrl\" class=\"btn-link\">\n    {{ 'loginForm.forgotPassword' | cxTranslate }}\n  </a>\n\n  <button\n    type=\"submit\"\n    class=\"btn btn-block btn-primary\"\n    [disabled]=\"form.disabled\"\n  >\n    {{ 'loginForm.signIn' | cxTranslate }}\n  </button>\n</form>\n\n<ng-template #requiredAsterisk>\n  <abbr\n    *cxFeature=\"'a11yRequiredAsterisks'\"\n    class=\"text-decoration-none\"\n    title=\"{{ 'common.required' | cxTranslate }}\"\n    >*</abbr\n  >\n</ng-template>\n", dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i2.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { kind: "directive", type: i2.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i2.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "directive", type: i1$2.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "component", type: i4.FormErrorsComponent, selector: "cx-form-errors", inputs: ["prefix", "fallbackPrefix", "translationParams", "control"] }, { kind: "component", type: i4.SpinnerComponent, selector: "cx-spinner" }, { kind: "directive", type: i4.PasswordVisibilityToggleDirective, selector: "[cxPasswordVisibilitySwitch][type=\"password\"]" }, { kind: "directive", type: i1.FeatureDirective, selector: "[cxFeature]", inputs: ["cxFeature"] }, { kind: "pipe", type: i1$1.AsyncPipe, name: "async" }, { kind: "pipe", type: i1.UrlPipe, name: "cxUrl" }, { kind: "pipe", type: i1.TranslatePipe, name: "cxTranslate" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: OneTimePasswordLoginFormComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cx-otp-login-form', changeDetection: ChangeDetectionStrategy.OnPush, template: "<cx-spinner class=\"overlay\" *ngIf=\"isUpdating$ | async\"></cx-spinner>\n\n<form (ngSubmit)=\"onSubmit()\" [formGroup]=\"form\">\n  <!-- TODO: (CXSPA-5953) Remove feature flags next major -->\n  <p *cxFeature=\"'a11yRequiredAsterisks'\" class=\"form-legend\">\n    {{ 'formLegend.required' | cxTranslate }}\n  </p>\n  <label>\n    <span class=\"label-content\">\n      {{ 'loginForm.emailAddress.label' | cxTranslate }}\n      <ng-template [ngTemplateOutlet]=\"requiredAsterisk\"></ng-template>\n    </span>\n    <input\n      required=\"true\"\n      type=\"email\"\n      class=\"form-control\"\n      formControlName=\"userId\"\n      placeholder=\"{{ 'loginForm.emailAddress.placeholder' | cxTranslate }}\"\n    />\n    <cx-form-errors [control]=\"form.get('userId')\"></cx-form-errors>\n  </label>\n\n  <label>\n    <span class=\"label-content\">\n      {{ 'loginForm.password.label' | cxTranslate }}\n      <ng-template [ngTemplateOutlet]=\"requiredAsterisk\"></ng-template>\n    </span>\n    <input\n      required=\"true\"\n      type=\"password\"\n      class=\"form-control\"\n      placeholder=\"{{ 'loginForm.password.placeholder' | cxTranslate }}\"\n      formControlName=\"password\"\n      [attr.aria-label]=\"'loginForm.password.placeholder' | cxTranslate\"\n      cxPasswordVisibilitySwitch\n    />\n    <cx-form-errors [control]=\"form.get('password')\"></cx-form-errors>\n  </label>\n\n  <a [routerLink]=\"{ cxRoute: 'forgotPassword' } | cxUrl\" class=\"btn-link\">\n    {{ 'loginForm.forgotPassword' | cxTranslate }}\n  </a>\n\n  <button\n    type=\"submit\"\n    class=\"btn btn-block btn-primary\"\n    [disabled]=\"form.disabled\"\n  >\n    {{ 'loginForm.signIn' | cxTranslate }}\n  </button>\n</form>\n\n<ng-template #requiredAsterisk>\n  <abbr\n    *cxFeature=\"'a11yRequiredAsterisks'\"\n    class=\"text-decoration-none\"\n    title=\"{{ 'common.required' | cxTranslate }}\"\n    >*</abbr\n  >\n</ng-template>\n" }]
        }], propDecorators: { style: [{
                type: HostBinding,
                args: ['class.user-form']
            }] } });

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
class OneTimePasswordLoginFormModeule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: OneTimePasswordLoginFormModeule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.5", ngImport: i0, type: OneTimePasswordLoginFormModeule, declarations: [OneTimePasswordLoginFormComponent], imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            RouterModule,
            UrlModule,
            I18nModule,
            FormErrorsModule,
            SpinnerModule,
            PasswordVisibilityToggleModule,
            FeaturesConfigModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: OneTimePasswordLoginFormModeule, providers: [
            provideDefaultConfig({
                cmsComponents: {
                    ReturningCustomerOTPLoginComponent: {
                        component: OneTimePasswordLoginFormComponent,
                        guards: [NotAuthGuard],
                    },
                },
            }),
        ], imports: [CommonModule,
            FormsModule,
            ReactiveFormsModule,
            RouterModule,
            UrlModule,
            I18nModule,
            FormErrorsModule,
            SpinnerModule,
            PasswordVisibilityToggleModule,
            FeaturesConfigModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: OneTimePasswordLoginFormModeule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule,
                        RouterModule,
                        UrlModule,
                        I18nModule,
                        FormErrorsModule,
                        SpinnerModule,
                        PasswordVisibilityToggleModule,
                        FeaturesConfigModule,
                    ],
                    providers: [
                        provideDefaultConfig({
                            cmsComponents: {
                                ReturningCustomerOTPLoginComponent: {
                                    component: OneTimePasswordLoginFormComponent,
                                    guards: [NotAuthGuard],
                                },
                            },
                        }),
                    ],
                    declarations: [OneTimePasswordLoginFormComponent],
                }]
        }] });

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
class VerificationTokenDialogComponent {
    constructor(launchDialogService) {
        this.launchDialogService = launchDialogService;
        this.VERIFICATION_TOKEN_DIALOG_ACTION = VERIFICATION_TOKEN_DIALOG_ACTION;
        this.focusConfig = {
            trap: true,
            block: true,
            autofocus: true,
            focusOnEscape: true,
        };
    }
    closeModal(reason) {
        this.launchDialogService.closeDialog(reason);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: VerificationTokenDialogComponent, deps: [{ token: i4.LaunchDialogService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.5", type: VerificationTokenDialogComponent, selector: "cx-verification-token-dialog", ngImport: i0, template: "<div\n  class=\"cx-verification-token-dialog cx-modal-container cx-asm-dialog\"\n  role=\"dialog\"\n  [cxFocus]=\"focusConfig\"\n  (esc)=\"closeModal(VERIFICATION_TOKEN_DIALOG_ACTION.OK)\"\n  aria-labelledby=\"verification-token-dialog-title\"\n>\n  <div class=\"cx-modal-content\">\n    <!-- Modal Header -->\n    <div class=\"cx-dialog-header modal-header\">\n      <div>\n        <span class=\"info-icon\">\n          <cx-icon type=\"INFO\"></cx-icon>\n        </span>\n        <h3\n          *cxFeature=\"'a11yDialogsHeading'\"\n          id=\"verification-token-dialog-title\"\n          class=\"title modal-title\"\n        >\n          {{ 'verificationTokenDialog.title' | cxTranslate }}\n        </h3>\n        <span\n          *cxFeature=\"'!a11yDialogsHeading'\"\n          id=\"verification-token-dialog-title\"\n          class=\"title modal-title\"\n        >\n          {{ 'verificationTokenDialog.title' | cxTranslate }}\n        </span>\n      </div>\n    </div>\n    <hr class=\"spliter\" />\n\n    <!-- Modal Body -->\n    <div class=\"cx-dialog-body modal-body\">\n      <div class=\"cx-dialog-row\">\n        <div class=\"cx-dialog-item\">\n          {{ 'verificationTokenDialog.noReceiveCode' | cxTranslate }}\n        </div>\n        <div class=\"cx-dialog-item\">\n          {{ 'verificationTokenDialog.contentLine1' | cxTranslate }}\n        </div>\n        <div class=\"cx-dialog-item\">\n          {{ 'verificationTokenDialog.contentLine2' | cxTranslate }}\n        </div>\n        <div class=\"cx-dialog-item\">\n          {{ 'verificationTokenDialog.contentLine3' | cxTranslate }}\n        </div>\n      </div>\n    </div>\n\n    <!-- Modal Footer -->\n    <div class=\"cx-dialog-footer modal-footer\">\n      <button\n        (click)=\"closeModal(VERIFICATION_TOKEN_DIALOG_ACTION.OK)\"\n        [attr.aria-label]=\"'verificationTokenDialog.ok' | cxTranslate\"\n        class=\"btn btn-primary\"\n        type=\"button\"\n      >\n        {{ 'verificationTokenDialog.close' | cxTranslate }}\n      </button>\n    </div>\n  </div>\n</div>\n", dependencies: [{ kind: "directive", type: i4.FocusDirective, selector: "[cxFocus]", inputs: ["cxFocus"] }, { kind: "component", type: i4.IconComponent, selector: "cx-icon,[cxIcon]", inputs: ["cxIcon", "type"] }, { kind: "directive", type: i1.FeatureDirective, selector: "[cxFeature]", inputs: ["cxFeature"] }, { kind: "pipe", type: i1.TranslatePipe, name: "cxTranslate" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: VerificationTokenDialogComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cx-verification-token-dialog', template: "<div\n  class=\"cx-verification-token-dialog cx-modal-container cx-asm-dialog\"\n  role=\"dialog\"\n  [cxFocus]=\"focusConfig\"\n  (esc)=\"closeModal(VERIFICATION_TOKEN_DIALOG_ACTION.OK)\"\n  aria-labelledby=\"verification-token-dialog-title\"\n>\n  <div class=\"cx-modal-content\">\n    <!-- Modal Header -->\n    <div class=\"cx-dialog-header modal-header\">\n      <div>\n        <span class=\"info-icon\">\n          <cx-icon type=\"INFO\"></cx-icon>\n        </span>\n        <h3\n          *cxFeature=\"'a11yDialogsHeading'\"\n          id=\"verification-token-dialog-title\"\n          class=\"title modal-title\"\n        >\n          {{ 'verificationTokenDialog.title' | cxTranslate }}\n        </h3>\n        <span\n          *cxFeature=\"'!a11yDialogsHeading'\"\n          id=\"verification-token-dialog-title\"\n          class=\"title modal-title\"\n        >\n          {{ 'verificationTokenDialog.title' | cxTranslate }}\n        </span>\n      </div>\n    </div>\n    <hr class=\"spliter\" />\n\n    <!-- Modal Body -->\n    <div class=\"cx-dialog-body modal-body\">\n      <div class=\"cx-dialog-row\">\n        <div class=\"cx-dialog-item\">\n          {{ 'verificationTokenDialog.noReceiveCode' | cxTranslate }}\n        </div>\n        <div class=\"cx-dialog-item\">\n          {{ 'verificationTokenDialog.contentLine1' | cxTranslate }}\n        </div>\n        <div class=\"cx-dialog-item\">\n          {{ 'verificationTokenDialog.contentLine2' | cxTranslate }}\n        </div>\n        <div class=\"cx-dialog-item\">\n          {{ 'verificationTokenDialog.contentLine3' | cxTranslate }}\n        </div>\n      </div>\n    </div>\n\n    <!-- Modal Footer -->\n    <div class=\"cx-dialog-footer modal-footer\">\n      <button\n        (click)=\"closeModal(VERIFICATION_TOKEN_DIALOG_ACTION.OK)\"\n        [attr.aria-label]=\"'verificationTokenDialog.ok' | cxTranslate\"\n        class=\"btn btn-primary\"\n        type=\"button\"\n      >\n        {{ 'verificationTokenDialog.close' | cxTranslate }}\n      </button>\n    </div>\n  </div>\n</div>\n" }]
        }], ctorParameters: () => [{ type: i4.LaunchDialogService }] });

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
const defaultVerificationTokenLayoutConfig = {
    launch: {
        ["ACCOUNT_VERIFICATION_TOKEN" /* LAUNCH_CALLER.ACCOUNT_VERIFICATION_TOKEN */]: {
            inlineRoot: true,
            component: VerificationTokenDialogComponent,
            dialogType: DIALOG_TYPE.DIALOG,
        },
    },
};

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
const globalMsgShowTime = 10000;
class VerificationTokenFormComponentService {
    constructor() {
        this.globalMessage = inject(GlobalMessageService);
        this.verificationTokenFacade = inject(VerificationTokenFacade);
        this.auth = inject(AuthService);
        this.busy$ = new BehaviorSubject(false);
        this.isUpdating$ = this.busy$.pipe(tap((state) => {
            state === true ? this.form.disable() : this.form.enable();
        }));
        this.form = new UntypedFormGroup({
            tokenId: new UntypedFormControl('', [Validators.required]),
            tokenCode: new UntypedFormControl('', [Validators.required]),
        });
    }
    login() {
        if (!this.form.valid) {
            this.form.markAllAsTouched();
            return;
        }
        this.busy$.next(true);
        from(this.auth.otpLoginWithCredentials(this.form.value.tokenId, this.form.value.tokenCode))
            .pipe(withLatestFrom(this.auth.isUserLoggedIn()), tap(([_, isLoggedIn]) => this.onSuccess(isLoggedIn)))
            .subscribe();
    }
    displayMessage(key, params) {
        this.globalMessage.add({
            key: key,
            params,
        }, GlobalMessageType.MSG_TYPE_CONFIRMATION, globalMsgShowTime);
    }
    createVerificationToken(loginId, password, purpose) {
        return this.verificationTokenFacade.createVerificationToken({
            loginId,
            password,
            purpose,
        });
    }
    onSuccess(isLoggedIn) {
        if (isLoggedIn) {
            // We want to remove error messages on successful login (primary the bad
            // username/password combination)
            this.globalMessage.remove(GlobalMessageType.MSG_TYPE_ERROR);
            this.form.reset();
        }
        this.busy$.next(false);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: VerificationTokenFormComponentService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: VerificationTokenFormComponentService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: VerificationTokenFormComponentService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [] });

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
class VerificationTokenFormComponent {
    constructor() {
        this.service = inject(VerificationTokenFormComponentService);
        this.launchDialogService = inject(LaunchDialogService);
        this.cdr = inject(ChangeDetectorRef);
        this.routingService = inject(RoutingService);
        this.waitTime = 60;
        this.form = this.service.form;
        this.isUpdating$ = this.service.isUpdating$;
        this.style = true;
        this.isResendDisabled = true;
    }
    ngOnInit() {
        if (!!history.state) {
            this.tokenId = history.state['tokenId'];
            this.password = history.state['password'];
            this.target = history.state['loginId'];
            history.pushState({
                tokenId: '',
                password: '',
                loginId: '',
            }, 'verifyToken');
            if (!this.target || !this.password || !this.tokenId) {
                this.service.displayMessage('verificationTokenForm.needInputCredentials', {});
                this.routingService.go(['/login']);
            }
            else {
                this.startWaitTimeInterval();
                this.service.displayMessage('verificationTokenForm.createVerificationToken', { target: this.target });
            }
        }
    }
    onSubmit() {
        this.service.login();
    }
    resendOTP() {
        this.isResendDisabled = true;
        this.resendLink.nativeElement.tabIndex = -1;
        this.resendLink.nativeElement.blur();
        this.waitTime = 60;
        this.startWaitTimeInterval();
        this.service
            .createVerificationToken(this.target, this.password, ONE_TIME_PASSWORD_LOGIN_PURPOSE)
            .subscribe({
            next: (result) => (this.tokenId = result.tokenId),
            complete: () => this.service.displayMessage('verificationTokenForm.createVerificationToken', { target: this.target }),
        });
    }
    startWaitTimeInterval() {
        const interval = setInterval(() => {
            this.waitTime--;
            this.cdr.detectChanges();
            if (this.waitTime <= 0) {
                clearInterval(interval);
                this.isResendDisabled = false;
                this.resendLink.nativeElement.tabIndex = 0;
                this.cdr.detectChanges();
            }
        }, 1000);
    }
    openInfoDailog() {
        this.launchDialogService.openDialogAndSubscribe("ACCOUNT_VERIFICATION_TOKEN" /* LAUNCH_CALLER.ACCOUNT_VERIFICATION_TOKEN */, this.element);
    }
    onOpenInfoDailogKeyDown(event) {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.openInfoDailog();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: VerificationTokenFormComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.5", type: VerificationTokenFormComponent, selector: "cx-verification-token-form", host: { properties: { "class.user-form": "this.style" } }, viewQueries: [{ propertyName: "element", first: true, predicate: ["noReceiveCodeLink"], descendants: true }, { propertyName: "resendLink", first: true, predicate: ["resendLink"], descendants: true }], ngImport: i0, template: "<cx-spinner class=\"overlay\" *ngIf=\"isUpdating$ | async\"></cx-spinner>\n\n<form (ngSubmit)=\"onSubmit()\" [formGroup]=\"form\">\n  <label>\n    <span class=\"label-content\">\n      {{ 'verificationTokenForm.verificationCode.label' | cxTranslate }}\n    </span>\n    <input\n      required=\"true\"\n      class=\"form-control\"\n      formControlName=\"tokenCode\"\n      [placeholder]=\"\n        'verificationTokenForm.verificationCode.placeholder' | cxTranslate\n      \"\n      aria-describedby=\"tokenInputHint\"\n    />\n    <cx-form-errors [control]=\"form.get('tokenCode')\"></cx-form-errors>\n  </label>\n  <span id=\"tokenInputHint\" class=\"cx-visually-hidden\">\n    {{ 'verificationTokenForm.tokenInputHint' | cxTranslate }}\n  </span>\n\n  <input type=\"hidden\" formControlName=\"tokenId\" [(ngModel)]=\"tokenId\" />\n\n  <div class=\"resend-link-text\">\n    <div class=\"left-text\">\n      <span role=\"timer\" aria-live=\"polite\" aria-atomic=\"true\">\n        <a\n          role=\"button\"\n          tabindex=\"-1\"\n          #resendLink\n          (keydown.enter)=\"resendOTP()\"\n          (keydown.space)=\"resendOTP()\"\n          (click)=\"resendOTP()\"\n          class=\"btn-link\"\n          [ngClass]=\"{ 'disabled-link': isResendDisabled }\"\n        >\n          {{ 'verificationTokenForm.resend' | cxTranslate }}\n        </a>\n        <ng-container *ngIf=\"isResendDisabled\">\n          {{\n            'verificationTokenForm.sendRateLime'\n              | cxTranslate: { waitTime: waitTime }\n          }}\n        </ng-container>\n      </span>\n    </div>\n    <div class=\"right-text\">\n      <a\n        role=\"button\"\n        tabindex=\"0\"\n        #noReceiveCodeLink\n        (keydown)=\"onOpenInfoDailogKeyDown($event)\"\n        (click)=\"openInfoDailog()\"\n        class=\"btn-link\"\n      >\n        {{ 'verificationTokenForm.noReceiveCode' | cxTranslate }}\n      </a>\n    </div>\n  </div>\n\n  <div class=\"verify-container\">\n    <button\n      type=\"submit\"\n      class=\"btn btn-block btn-primary\"\n      [disabled]=\"form.disabled\"\n    >\n      {{ 'verificationTokenForm.verify' | cxTranslate }}\n    </button>\n    <button\n      [routerLink]=\"{ cxRoute: 'login' } | cxUrl\"\n      class=\"btn btn-block btn-secondary btn-register\"\n    >\n      {{ 'verificationTokenForm.back' | cxTranslate }}\n    </button>\n  </div>\n</form>\n", dependencies: [{ kind: "directive", type: i1$1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i2.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { kind: "directive", type: i2.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i2.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "directive", type: i1$2.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "component", type: i4.FormErrorsComponent, selector: "cx-form-errors", inputs: ["prefix", "fallbackPrefix", "translationParams", "control"] }, { kind: "component", type: i4.SpinnerComponent, selector: "cx-spinner" }, { kind: "pipe", type: i1$1.AsyncPipe, name: "async" }, { kind: "pipe", type: i1.UrlPipe, name: "cxUrl" }, { kind: "pipe", type: i1.TranslatePipe, name: "cxTranslate" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: VerificationTokenFormComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cx-verification-token-form', changeDetection: ChangeDetectionStrategy.OnPush, template: "<cx-spinner class=\"overlay\" *ngIf=\"isUpdating$ | async\"></cx-spinner>\n\n<form (ngSubmit)=\"onSubmit()\" [formGroup]=\"form\">\n  <label>\n    <span class=\"label-content\">\n      {{ 'verificationTokenForm.verificationCode.label' | cxTranslate }}\n    </span>\n    <input\n      required=\"true\"\n      class=\"form-control\"\n      formControlName=\"tokenCode\"\n      [placeholder]=\"\n        'verificationTokenForm.verificationCode.placeholder' | cxTranslate\n      \"\n      aria-describedby=\"tokenInputHint\"\n    />\n    <cx-form-errors [control]=\"form.get('tokenCode')\"></cx-form-errors>\n  </label>\n  <span id=\"tokenInputHint\" class=\"cx-visually-hidden\">\n    {{ 'verificationTokenForm.tokenInputHint' | cxTranslate }}\n  </span>\n\n  <input type=\"hidden\" formControlName=\"tokenId\" [(ngModel)]=\"tokenId\" />\n\n  <div class=\"resend-link-text\">\n    <div class=\"left-text\">\n      <span role=\"timer\" aria-live=\"polite\" aria-atomic=\"true\">\n        <a\n          role=\"button\"\n          tabindex=\"-1\"\n          #resendLink\n          (keydown.enter)=\"resendOTP()\"\n          (keydown.space)=\"resendOTP()\"\n          (click)=\"resendOTP()\"\n          class=\"btn-link\"\n          [ngClass]=\"{ 'disabled-link': isResendDisabled }\"\n        >\n          {{ 'verificationTokenForm.resend' | cxTranslate }}\n        </a>\n        <ng-container *ngIf=\"isResendDisabled\">\n          {{\n            'verificationTokenForm.sendRateLime'\n              | cxTranslate: { waitTime: waitTime }\n          }}\n        </ng-container>\n      </span>\n    </div>\n    <div class=\"right-text\">\n      <a\n        role=\"button\"\n        tabindex=\"0\"\n        #noReceiveCodeLink\n        (keydown)=\"onOpenInfoDailogKeyDown($event)\"\n        (click)=\"openInfoDailog()\"\n        class=\"btn-link\"\n      >\n        {{ 'verificationTokenForm.noReceiveCode' | cxTranslate }}\n      </a>\n    </div>\n  </div>\n\n  <div class=\"verify-container\">\n    <button\n      type=\"submit\"\n      class=\"btn btn-block btn-primary\"\n      [disabled]=\"form.disabled\"\n    >\n      {{ 'verificationTokenForm.verify' | cxTranslate }}\n    </button>\n    <button\n      [routerLink]=\"{ cxRoute: 'login' } | cxUrl\"\n      class=\"btn btn-block btn-secondary btn-register\"\n    >\n      {{ 'verificationTokenForm.back' | cxTranslate }}\n    </button>\n  </div>\n</form>\n" }]
        }], ctorParameters: () => [], propDecorators: { style: [{
                type: HostBinding,
                args: ['class.user-form']
            }], element: [{
                type: ViewChild,
                args: ['noReceiveCodeLink']
            }], resendLink: [{
                type: ViewChild,
                args: ['resendLink']
            }] } });

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
class VerificationTokenFormModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: VerificationTokenFormModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.5", ngImport: i0, type: VerificationTokenFormModule, declarations: [VerificationTokenFormComponent,
            VerificationTokenDialogComponent], imports: [CommonModule,
            FormsModule,
            KeyboardFocusModule,
            ReactiveFormsModule,
            RouterModule,
            UrlModule,
            IconModule,
            I18nModule,
            FormErrorsModule,
            SpinnerModule,
            FeaturesConfigModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: VerificationTokenFormModule, providers: [
            provideDefaultConfig({
                cmsComponents: {
                    VerifyOTPTokenComponent: {
                        component: VerificationTokenFormComponent,
                        guards: [NotAuthGuard],
                        providers: [
                            {
                                provide: VerificationTokenFormComponentService,
                                useClass: VerificationTokenFormComponentService,
                                deps: [
                                    AuthService,
                                    GlobalMessageService,
                                    VerificationTokenFacade,
                                    WindowRef,
                                ],
                            },
                        ],
                    },
                },
            }),
            provideDefaultConfig(defaultVerificationTokenLayoutConfig),
        ], imports: [CommonModule,
            FormsModule,
            KeyboardFocusModule,
            ReactiveFormsModule,
            RouterModule,
            UrlModule,
            IconModule,
            I18nModule,
            FormErrorsModule,
            SpinnerModule,
            FeaturesConfigModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: VerificationTokenFormModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        KeyboardFocusModule,
                        ReactiveFormsModule,
                        RouterModule,
                        UrlModule,
                        IconModule,
                        I18nModule,
                        FormErrorsModule,
                        SpinnerModule,
                        FeaturesConfigModule,
                    ],
                    providers: [
                        provideDefaultConfig({
                            cmsComponents: {
                                VerifyOTPTokenComponent: {
                                    component: VerificationTokenFormComponent,
                                    guards: [NotAuthGuard],
                                    providers: [
                                        {
                                            provide: VerificationTokenFormComponentService,
                                            useClass: VerificationTokenFormComponentService,
                                            deps: [
                                                AuthService,
                                                GlobalMessageService,
                                                VerificationTokenFacade,
                                                WindowRef,
                                            ],
                                        },
                                    ],
                                },
                            },
                        }),
                        provideDefaultConfig(defaultVerificationTokenLayoutConfig),
                    ],
                    declarations: [
                        VerificationTokenFormComponent,
                        VerificationTokenDialogComponent,
                    ],
                }]
        }] });

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
class UserAccountComponentsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: UserAccountComponentsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.5", ngImport: i0, type: UserAccountComponentsModule, imports: [LoginModule,
            LoginFormModule,
            VerificationTokenFormModule,
            LoginRegisterModule,
            MyAccountV2UserModule,
            OneTimePasswordLoginFormModeule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: UserAccountComponentsModule, imports: [LoginModule,
            LoginFormModule,
            VerificationTokenFormModule,
            LoginRegisterModule,
            MyAccountV2UserModule,
            OneTimePasswordLoginFormModeule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.5", ngImport: i0, type: UserAccountComponentsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        LoginModule,
                        LoginFormModule,
                        VerificationTokenFormModule,
                        LoginRegisterModule,
                        MyAccountV2UserModule,
                        OneTimePasswordLoginFormModeule,
                    ],
                }]
        }] });

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/**
 * Generated bundle index. Do not edit.
 */

export { LoginComponent, LoginFormComponent, LoginFormComponentService, LoginFormModule, LoginModule, LoginRegisterComponent, LoginRegisterModule, MyAccountV2UserComponent, MyAccountV2UserModule, OneTimePasswordLoginFormComponent, OneTimePasswordLoginFormModeule, UserAccountComponentsModule, VerificationTokenFormComponent, VerificationTokenFormComponentService, VerificationTokenFormModule };
